import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import {
  Button,
  Col,
  ControlLabel,
  Clearfix,
  FormControl,
  FormGroup,
  Nav,
  NavItem,
  Panel,
  Table,
} from 'react-bootstrap';
import { get, isEmpty } from 'lodash';
import {
  CreditRating,
  FinanceType,
  CLIENT_TYPE,
  CarStatus,
} from '@upstart/harmony/lib/constants';
import { getPaymentOffers } from '@upstart/harmony/lib/payment-utils';
import PaymentsService from '../../services/PaymentsService';
import { getPeriodicPaymentFromOffer } from '../../util/paymentUtils';
import CarImage from '../CarImage';

class CarValidator extends PureComponent {
  // eslint-disable-next-line react/sort-comp
  constructor(props) {
    super(props);
    this.state = {
      clientType: CLIENT_TYPE.IN_STORE,
      financeType: FinanceType.PURCHASE,
      creditRating: CreditRating.EXCELLENT,
      downPayment: undefined,
    };
    this.updatePayments();
  }

  useBiweekly = () => get(this.props.dealer, 'desking.showBiweeklyPayments');

  updatePayments = () => {
    const { dealer, car } = this.props;
    const { clientType, creditRating, downPayment } = this.state;
    PaymentsService.getPaymentsForCar(
      clientType,
      dealer.id,
      car.id,
      creditRating,
      downPayment,
    ).then(payments => {
      this.setState({ payments });
    });
  };

  updateClientType = async event => {
    await this.setState({ clientType: event.target.value });
    this.updatePayments();
  };

  updateFinanceType = financeType => {
    this.setState({ financeType });
    // all three finance types are returned in the payments object so we don't need to re-fetch them
  };

  updateCreditRating = async event => {
    await this.setState({ creditRating: event.target.value });
    this.updatePayments();
  };

  updateDownPayment = async event => {
    const downPayment = !isEmpty(event.target.value)
      ? parseInt(event.target.value, 10)
      : 0;
    await this.setState({ downPayment });
    this.updatePayments();
  };

  renderHeader = defaultDownPayment => {
    const { car, dealer } = this.props;
    const { clientType, creditRating, downPayment } = this.state;
    return (
      <Panel.Heading className="form-inline">
        <Col lg={2}>
          <CarImage className="car-validator-image" car={car} dealer={dealer} />
        </Col>
        <Col lg={10}>
          <h4 className="car-validator-title">
            {`${car.name} - ${car.id} - ${car.stock}`}
            {car.status === CarStatus.SALE_PENDING && (
              <Button
                className="car-validator-mark-available"
                onClick={this.props.onMarkCarAvailable}
              >
                Mark as Available
              </Button>
            )}
          </h4>
          <FormGroup>
            <ControlLabel>Client Type</ControlLabel>
            <FormControl
              componentClass="select"
              value={clientType}
              onChange={this.updateClientType}
            >
              {Object.values(CLIENT_TYPE)
                .map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))
                .reverse()}
            </FormControl>
          </FormGroup>
          <FormGroup>
            <ControlLabel>Credit Rating</ControlLabel>
            <FormControl
              componentClass="select"
              value={creditRating}
              onChange={this.updateCreditRating}
            >
              {Object.values(CreditRating)
                .map(rating => (
                  <option key={rating} value={rating}>
                    {rating}
                  </option>
                ))
                .reverse()}
            </FormControl>
          </FormGroup>
          <FormGroup>
            <ControlLabel>Down Payment</ControlLabel>
            <FormControl
              type="number"
              value={downPayment}
              placeholder={defaultDownPayment}
              onChange={this.updateDownPayment}
            />
          </FormGroup>
        </Col>
        <Clearfix />
      </Panel.Heading>
    );
  };

  renderPurchaseOffers = offers => (
    <Table striped bordered condensed hover>
      <thead>
        <tr>
          <th>Term</th>
          <th>APR</th>
          <th>Rebate</th>
          <th>{this.useBiweekly() ? 'Biweekly' : 'Monthly'} Payment</th>
        </tr>
      </thead>
      <tbody>
        {offers &&
          offers.map(o => (
            <tr>
              <td>{o.months}</td>
              <td>{o.apr}</td>
              <td>{o.rebate}</td>
              <td>{getPeriodicPaymentFromOffer(o, this.useBiweekly())}</td>
            </tr>
          ))}
      </tbody>
    </Table>
  );

  renderLeaseOffers = offers => (
    <Table striped bordered condensed hover>
      <thead>
        <tr>
          <th>Term</th>
          <th>Mileage</th>
          <th>Residual</th>
          <th>Rebate</th>
          <th>Rate</th>
          <th>{this.useBiweekly() ? 'Biweekly' : 'Monthly'} Payment</th>
        </tr>
      </thead>
      <tbody>
        {offers &&
          offers.map(o => (
            <tr>
              <td>{o.months}</td>
              <td>{o.mileage}</td>
              <td>{o.residual}</td>
              <td>{o.rebate}</td>
              <td>{o.rate}</td>
              <td>{getPeriodicPaymentFromOffer(o, this.useBiweekly())}</td>
            </tr>
          ))}
      </tbody>
    </Table>
  );

  renderCashOffers = offers => (
    <Table striped bordered condensed hover>
      <thead>
        <tr>
          <th>Rebate</th>
        </tr>
      </thead>
      <tbody>
        {offers &&
          offers.map(o => (
            <tr>
              <td>{o.rebate}</td>
            </tr>
          ))}
      </tbody>
    </Table>
  );

  renderSummary = firstOffer => (
    <Table striped bordered condensed hover>
      <thead>
        <tr>
          <th>Vehicle Price</th>
          <th>Addons</th>
          <th>Discount</th>
          <th>Dealer Cash</th>
          <th>Dealer Fees</th>
          <th>Tax</th>
          <th>Tax Breakdown</th>
          <th>Sales Tax</th>
          <th>Balance</th>
          <th>Front Gross</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{firstOffer.displayPrice}</td>
          <td>{firstOffer.upgrades}</td>
          <td>{firstOffer.discount}</td>
          <td>{firstOffer.dealerCash}</td>
          <td>{firstOffer.serviceFees}</td>
          <td>{firstOffer.tax}</td>
          <td>
            {Object.values(firstOffer.taxBreakdown).map(entry => (
              <div>
                {entry.name} - {entry.rate}
              </div>
            ))}
          </td>
          <td>{firstOffer.taxAmount}</td>
          <td>{firstOffer.balance}</td>
          <td>{firstOffer.gross}</td>
        </tr>
      </tbody>
    </Table>
  );

  render() {
    const { financeType, payments } = this.state;
    const offers = payments && getPaymentOffers(payments, financeType);
    const firstOffer = offers && offers[0];
    return (
      <div className="car-validator">
        <Panel>
          {this.renderHeader(firstOffer ? firstOffer.downPayment : undefined)}
          <Panel.Body>
            <Nav bsStyle="tabs" activeKey="1" onSelect={this.updateFinanceType}>
              <NavItem
                eventKey={FinanceType.PURCHASE}
                active={financeType === FinanceType.PURCHASE}
              >
                Purchase
              </NavItem>
              <NavItem
                eventKey={FinanceType.LEASE}
                active={financeType === FinanceType.LEASE}
              >
                Lease
              </NavItem>
              <NavItem
                eventKey={FinanceType.CASH}
                active={financeType === FinanceType.CASH}
              >
                Cash
              </NavItem>
            </Nav>
            {financeType === FinanceType.PURCHASE &&
              this.renderPurchaseOffers(offers)}
            {financeType === FinanceType.LEASE &&
              this.renderLeaseOffers(offers)}
            {financeType === FinanceType.CASH && this.renderCashOffers(offers)}
            {firstOffer && this.renderSummary(firstOffer)}
          </Panel.Body>
        </Panel>
      </div>
    );
  }
}
CarValidator.propTypes = {
  dealer: PropTypes.object,
  car: PropTypes.object,
  onMarkCarAvailable: PropTypes.func,
};

export default CarValidator;
